/**
 * Who is signed in, and how the rest of the app gets a token.
 *
 * The API client asks for a token per request via `getAccessToken` rather than
 * being handed one at sign-in, because an 8-hour token will expire while a tab
 * is open and the refresh has to be invisible.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { currentSession, signIn as cognitoSignIn, signOut as cognitoSignOut, type Session } from './cognito';

type AuthState =
  | { status: 'restoring' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; session: Session };

interface AuthContextValue {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Called by the API client. Refreshes silently when the token is stale. */
  getAccessToken: () => Promise<string | null>;
  adoptSession: (session: Session) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Refresh slightly early so a request never leaves with a just-expired token. */
const EXPIRY_SKEW_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'restoring' });
  // Read inside getAccessToken without making that callback depend on renders.
  const sessionRef = useRef<Session | null>(null);

  const setSession = useCallback((session: Session | null) => {
    sessionRef.current = session;
    setState(session ? { status: 'signedIn', session } : { status: 'signedOut' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    currentSession()
      .then((session) => {
        if (!cancelled) setSession(session);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setSession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setSession(await cognitoSignIn(email, password));
    },
    [setSession]
  );

  const signOut = useCallback(() => {
    cognitoSignOut();
    setSession(null);
  }, [setSession]);

  const getAccessToken = useCallback(async () => {
    const session = sessionRef.current;
    if (session && session.expiresAt - EXPIRY_SKEW_MS > Date.now()) {
      return session.accessToken;
    }
    // Expired or absent: let Cognito use the refresh token.
    const refreshed = await currentSession();
    setSession(refreshed);
    return refreshed?.accessToken ?? null;
  }, [setSession]);

  const value = useMemo(
    () => ({ state, signIn, signOut, getAccessToken, adoptSession: setSession }),
    [state, signIn, signOut, getAccessToken, setSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
